import React, { useState, useContext } from "react";
import { GlobalContext } from "../../contexts/GlobalContext";
import * as Yup from "yup";
import { Formik } from "formik";
import Cookies from "js-cookie";
import {
  Box,
  Grid,
  Card as MuiCard,
  CardContent,
  TextField as MuiTextField,
  Button as MuiButton,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import { spacing } from "@mui/system";
import styled from "@emotion/styled";
import PersonIcon from "@mui/icons-material/Person";
import SecurityIcon from "@mui/icons-material/Security";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import MfaSettings from "./MfaSettings";
import Notification from "../../components/Notification";

const Card = styled(MuiCard)(spacing);
const TextField = styled(MuiTextField)(spacing);
const Button = styled(MuiButton)(spacing);

const Profile = () => {
  const { t } = useTranslation();
  const { displayName, email, userName, companyName } = useContext(GlobalContext);
  const [activeSection, setActiveSection] = useState("accountInfo");
  const [securityExpanded, setSecurityExpanded] = useState(true);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: "", type: "success" });

  const showNotify = (type, message) => {
    setNotify({ isOpen: true, message, type });
  };

  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().required(t("Current password is required")),
    newPassword: Yup.string()
      .min(8, t("Password must be at least 8 characters long"))
      .matches(/[a-z]/, t("Password must contain lowercase letters"))
      .matches(/[A-Z]/, t("Password must contain uppercase letters"))
      .matches(/[0-9]/, t("Password must contain at least one number"))
      .matches(/[^a-zA-Z0-9]/, t("Password must contain at least one special character"))
      .required(t("New password is required")),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], t("Passwords do not match"))
      .required(t("Confirm password is required")),
  });

  const handleToggleNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setNotify((prev) => ({ ...prev, isOpen: false }));

    // Client-side validation
    if (!values.currentPassword || !values.newPassword || !values.confirmPassword) {
      showNotify("error", t("Please fill in all fields"));
      setSubmitting(false);
      return;
    }

    if (values.newPassword !== values.confirmPassword) {
      showNotify("error", t("New password and confirmation do not match"));
      setSubmitting(false);
      return;
    }

    if (values.newPassword.length < 8) {
      showNotify("error", t("New password must be at least 8 characters long"));
      setSubmitting(false);
      return;
    }

    try {
      setSubmitting(true);

      // Try sessionStorage first, otherwise Cookies (as used in the rest of the codebase)
      const token = sessionStorage.getItem("accessToken") || Cookies.get("accessToken");

      if (!token) {
        showNotify("error", t("Authentication token not found. Please log in again."));
        setSubmitting(false);
        return;
      }

      const response = await fetch("/api/v1/change-password", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        }),
      });

      if (response.ok) {
        showNotify("success", t("Password successfully changed!"));
        resetForm();
      } else {
        const errorData = await response.json().catch(() => ({}));
        showNotify(
          "error",
          errorData.message ||
            t("Failed to update password ({{status}}). Please check your current password.", {
              status: response.status,
            })
        );
      }
    } catch (err) {
      showNotify("error", t("Failed to update password. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  };

  return (
    <Box sx={{ display: "flex", gap: 3, minHeight: "100%" }}>
      {/* Left Sidebar - Navigation */}
      <Paper
        elevation={1}
        sx={{
          width: 280,
          p: 2,
          height: "fit-content",
          position: "sticky",
          top: 20,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t("Profile Settings")}
        </Typography>
        <List>
          <ListItem disablePadding>
            <ListItemButton
              selected={activeSection === "accountInfo"}
              onClick={() => setActiveSection("accountInfo")}
              sx={{
                borderRadius: 1,
                "&.Mui-selected": {
                  backgroundColor: "primary.main",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                },
              }}
            >
              <ListItemIcon sx={{ color: activeSection === "accountInfo" ? "white" : "inherit" }}>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText primary={t("Account Information")} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding sx={{ mt: 0.5 }}>
            <ListItemButton
              onClick={() => setSecurityExpanded(!securityExpanded)}
              sx={{
                borderRadius: 1,
              }}
            >
              <ListItemIcon>
                <SecurityIcon />
              </ListItemIcon>
              <ListItemText primary={t("Security Settings")} />
              {securityExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>
          </ListItem>
          {securityExpanded && (
            <>
              <ListItem disablePadding sx={{ pl: 4, mt: 0.5 }}>
                <ListItemButton
                  selected={activeSection === "changePassword"}
                  onClick={() => setActiveSection("changePassword")}
                  sx={{
                    borderRadius: 1,
                    "&.Mui-selected": {
                      backgroundColor: "primary.main",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "primary.dark",
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: activeSection === "changePassword" ? "white" : "inherit" }}>
                    <LockIcon />
                  </ListItemIcon>
                  <ListItemText primary={t("Change Password")} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding sx={{ pl: 4, mt: 0.5 }}>
                <ListItemButton
                  selected={activeSection === "mfaSettings"}
                  onClick={() => setActiveSection("mfaSettings")}
                  sx={{
                    borderRadius: 1,
                    "&.Mui-selected": {
                      backgroundColor: "primary.main",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "primary.dark",
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: activeSection === "mfaSettings" ? "white" : "inherit" }}>
                    <QrCode2Icon />
                  </ListItemIcon>
                  <ListItemText primary={t("MFA Settings")} />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Paper>

      {/* Main Content Area */}
      <Box sx={{ flex: 1 }}>
        {activeSection === "accountInfo" && (
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <PersonIcon sx={{ mr: 2, fontSize: 28 }} />
                <Typography variant="h5" component="h1">
                  {t("Account Information")}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <TableContainer>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: "30%" }}>
                        {t("Display Name")}
                      </TableCell>
                      <TableCell>{displayName || "-"}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: "30%" }}>
                        {t("Email")}
                      </TableCell>
                      <TableCell>{email || "-"}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: "30%" }}>
                        {t("Username")}
                      </TableCell>
                      <TableCell>{userName || "-"}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: "30%" }}>
                        {t("Company Name")}
                      </TableCell>
                      <TableCell>{companyName || "-"}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {activeSection === "changePassword" && (
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <SecurityIcon sx={{ mr: 2, fontSize: 28 }} />
                <Typography variant="h5" component="h1">
                  {t("Change Password")}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t("Update your password")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                {t("Choose a strong password that you haven't used before. It should be at least 8 characters long.")}
              </Typography>

              <Notification notify={notify} setNotify={setNotify} />

              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="currentPassword"
                          label={t("Current Password")}
                          type="password"
                          value={values.currentPassword}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.currentPassword && Boolean(errors.currentPassword)}
                          helperText={touched.currentPassword && errors.currentPassword}
                          placeholder={t("Enter your current password")}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="newPassword"
                          label={t("New Password")}
                          type={showNewPassword ? "text" : "password"}
                          value={values.newPassword}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.newPassword && Boolean(errors.newPassword)}
                          helperText={touched.newPassword && errors.newPassword}
                          placeholder={t("Enter your new password")}
                          variant="outlined"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label={t("Toggle password visibility")}
                                  onClick={handleToggleNewPassword}
                                  edge="end"
                                  size="small"
                                >
                                  {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="confirmPassword"
                          label={t("Confirm New Password")}
                          type={showConfirmPassword ? "text" : "password"}
                          value={values.confirmPassword}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                          helperText={touched.confirmPassword && errors.confirmPassword}
                          placeholder={t("Confirm your new password")}
                          variant="outlined"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label={t("Toggle password visibility")}
                                  onClick={handleToggleConfirmPassword}
                                  edge="end"
                                  size="small"
                                >
                                  {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setNotify((prev) => ({ ...prev, isOpen: false }));
                            }}
                            disabled={isSubmitting}
                          >
                            {t("Cancel")}
                          </Button>
                          <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                          >
                            {isSubmitting ? t("Updating...") : t("Update Password")}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </form>
                )}
              </Formik>
            </CardContent>
          </Card>
        )}

        {activeSection === "mfaSettings" && <MfaSettings />}
      </Box>

      {/* Right Sidebar - Password Requirements */}
      {activeSection === "changePassword" && (
        <Paper
          elevation={1}
          sx={{
            width: 280,
            p: 2,
            height: "fit-content",
            position: "sticky",
            top: 20,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t("Password Requirements")}
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary={t("At least 8 characters long")} />
            </ListItem>
            <ListItem>
              <ListItemText primary={t("Contains uppercase and lowercase letters")} />
            </ListItem>
            <ListItem>
              <ListItemText primary={t("Contains at least one number")} />
            </ListItem>
            <ListItem>
              <ListItemText primary={t("Contains at least one special character")} />
            </ListItem>
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default Profile;

